/**
 * Sistema de Caché Avanzado para SEOgenia
 * Utiliza Cache Storage API para máximo rendimiento
 */

interface CacheConfig {
  cacheName: string;
  maxAge: number; // en milisegundos
  maxItems?: number;
}

class CacheManager {
  private config: CacheConfig;

  constructor(config: CacheConfig) {
    this.config = {
      maxItems: 100,
      ...config,
    };
  }

  /**
   * Guardar datos en caché
   */
  async set(key: string, data: unknown): Promise<void> {
    try {
      const cache = await caches.open(this.config.cacheName);
      const response = new Response(JSON.stringify({
        data,
        timestamp: Date.now(),
      }), {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': `max-age=${this.config.maxAge / 1000}`,
        },
      });
      
      await cache.put(new Request(key), response);
      await this.cleanOldEntries();
    } catch (error) {
      console.error('Error al guardar en caché:', error);
    }
  }

  /**
   * Obtener datos de caché
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const cache = await caches.open(this.config.cacheName);
      const response = await cache.match(new Request(key));
      
      if (!response) {
        return null;
      }

      const cached = await response.json();
      const age = Date.now() - cached.timestamp;

      // Verificar si el caché ha expirado
      if (age > this.config.maxAge) {
        await this.delete(key);
        return null;
      }

      return cached.data as T;
    } catch (error) {
      console.error('Error al leer caché:', error);
      return null;
    }
  }

  /**
   * Eliminar entrada específica
   */
  async delete(key: string): Promise<void> {
    try {
      const cache = await caches.open(this.config.cacheName);
      await cache.delete(new Request(key));
    } catch (error) {
      console.error('Error al eliminar caché:', error);
    }
  }

  /**
   * Limpiar todo el caché
   */
  async clear(): Promise<void> {
    try {
      await caches.delete(this.config.cacheName);
    } catch (error) {
      console.error('Error al limpiar caché:', error);
    }
  }

  /**
   * Limpiar entradas antiguas si excede el límite
   */
  private async cleanOldEntries(): Promise<void> {
    try {
      const cache = await caches.open(this.config.cacheName);
      const keys = await cache.keys();

      if (keys.length > (this.config.maxItems || 100)) {
        // Eliminar las entradas más antiguas
        const toDelete = keys.slice(0, keys.length - (this.config.maxItems || 100));
        await Promise.all(toDelete.map(key => cache.delete(key)));
      }
    } catch (error) {
      console.error('Error al limpiar entradas antiguas:', error);
    }
  }

  /**
   * Obtener o establecer (patrón cache-aside)
   */
  async getOrSet<T>(
    key: string,
    fetcher: () => Promise<T>
  ): Promise<T> {
    // Intentar obtener de caché
    const cached = await this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Si no está en caché, obtener datos frescos
    const fresh = await fetcher();
    await this.set(key, fresh);
    return fresh;
  }
}

// Instancias de caché para diferentes propósitos
export const apiCache = new CacheManager({
  cacheName: 'seogenia-api-cache',
  maxAge: 5 * 60 * 1000, // 5 minutos
  maxItems: 50,
});

export const imageCache = new CacheManager({
  cacheName: 'seogenia-image-cache',
  maxAge: 24 * 60 * 60 * 1000, // 24 horas
  maxItems: 200,
});

export const dataCache = new CacheManager({
  cacheName: 'seogenia-data-cache',
  maxAge: 60 * 60 * 1000, // 1 hora
  maxItems: 100,
});

/**
 * Hook para usar caché en componentes React
 */
export function useCachedData<T>(
  key: string,
  fetcher: () => Promise<T>,
  cacheManager: CacheManager = dataCache
) {
  return cacheManager.getOrSet(key, fetcher);
}

/**
 * Precarga de recursos críticos
 */
export async function preloadCriticalResources(): Promise<void> {
  if ('caches' in window) {
    const cache = await caches.open('seogenia-critical-cache');
    
    const criticalResources = [
      '/',
      '/servicios/arquitectura-semantica',
      '/servicios/schema-markup-avanzado',
      '/servicios/seo-agentes-ia',
    ];

    await Promise.all(
      criticalResources.map(url => 
        fetch(url).then(response => cache.put(url, response.clone()))
      )
    );
  }
}

/**
 * Limpiar todos los cachés
 */
export async function clearAllCaches(): Promise<void> {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(name => caches.delete(name))
    );
  }
}

export default CacheManager;
